import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { Reveal } from '../components/motion';
import SEO from '../components/SEO';
import { SectionTag, ArrowBtn } from '../components/bits';
import { articleBySlug, ARTICLES } from '../content/articles';
import { API } from '../content/data';

const coverSrc = (c) => (c?.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${c}` : c);

function Block({ title, html }) {
  if (!html) return null;
  return (
    <Reveal className="border-t border-navy/10 py-10">
      <h2 className="overline text-electric">{title}</h2>
      <div className="article-body mt-4 max-w-3xl text-base leading-relaxed text-navy/80 sm:text-lg" dangerouslySetInnerHTML={{ __html: html }} />
    </Reveal>
  );
}

export default function Article() {
  const { slug } = useParams();
  const [remote, setRemote] = useState(null);
  useEffect(() => {
    setRemote(null);
    axios.get(`${API}/articles/${slug}`).then((r) => setRemote(r.data)).catch(() => {});
  }, [slug]);
  const article = remote || articleBySlug(slug);
  if (!article) {
    return (
      <div className="wrap py-40 text-center">
        <h1 className="text-h2 font-semibold">Story not found.</h1>
        <Link to="/press" className="mt-6 inline-block text-sm font-semibold text-electric">← Back to Press & Insights</Link>
      </div>
    );
  }
  const relatedPool = ARTICLES.filter((a) => a.slug !== slug);
  const related = relatedPool.slice(0, 3);
  return (
    <div data-testid="article-page">
      <SEO title={article.title} description={article.summary} image={coverSrc(article.cover)} path={`/press/${article.slug}`} type="article" />
      <header className="bg-grid-dark relative overflow-hidden bg-navy text-white">
        <div className="wrap relative pb-16 pt-36 sm:pb-24 sm:pt-44">
          <SectionTag dark>{article.category}</SectionTag>
          <Reveal delay={0.05}><h1 className="text-display mt-6 max-w-4xl font-semibold">{article.title}</h1></Reveal>
          <Reveal delay={0.12}>
            <p className="mt-6 max-w-2xl font-serif text-lg italic leading-relaxed text-white/75 sm:text-xl">{article.summary}</p>
            <p className="mt-6 font-mono text-xs uppercase tracking-[0.2em] text-white/45">{article.date} | {article.readTime}</p>
          </Reveal>
          {article.cover && (
            <Reveal delay={0.2} className="mt-10">
              <img src={coverSrc(article.cover)} alt={article.title} className="aspect-[21/9] w-full rounded-3xl border border-white/10 object-cover" loading="lazy" />
            </Reveal>
          )}
        </div>
      </header>

      <article className="bg-paper py-16 sm:py-20">
        <div className="wrap max-w-4xl">
          <Block title="The Question" html={article.question} />
          <Block title="The Context" html={article.context} />
          <Block title="The DITEC Perspective" html={article.perspective} />
          <Block title="Example" html={article.example} />
          <Block title="What This Could Mean" html={article.meaning} />
          <Reveal className="border-t border-navy/10 py-10">
            <p className="rounded-2xl border border-navy/10 bg-white p-6 text-xs leading-relaxed text-navy/55">
              Editorial Note: Any hypothetical, simulated or modelled example above is clearly labelled as such and must not be presented as an achieved DITEC result.
            </p>
          </Reveal>
        </div>
      </article>

      <section className="bg-white py-16 sm:py-24">
        <div className="wrap">
          <SectionTag>Related Stories</SectionTag>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {related.map((a) => (
              <Link key={a.slug} to={`/press/${a.slug}`} data-testid={`related-${a.slug}`} className="group flex flex-col rounded-3xl border border-navy/10 bg-paper p-7 transition-[transform,box-shadow] duration-500 hover:-translate-y-1 hover:shadow-xl hover:shadow-navy/10">
                <span className="overline !text-[10px] text-electric">{a.category}</span>
                <h3 className="mt-3 text-base font-semibold leading-snug tracking-tight group-hover:text-electric">{a.title}</h3>
                <span className="mt-auto pt-5 font-mono text-xs text-navy/45">{a.date} · {a.readTime}</span>
              </Link>
            ))}
          </div>
          <Reveal className="mt-12"><ArrowBtn to="/about" variant="navy" testId="article-cta-explore">Explore DITEC</ArrowBtn></Reveal>
        </div>
      </section>
    </div>
  );
}
